import React from 'react';

import { useFormikContext } from 'formik';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Chip,
  InputLabel,
  TextField,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { ReactComponent as DropdownIcon } from 'assets/icons/IconDropdown.svg';
import MenuItem from '@mui/material/MenuItem';

export enum DropdownOptionActiveState {
  YES = 'Y',
  NO = 'N',
}
export interface DropdownOption {
  label: string;
  value: any;
  isActive?: 'Y' | 'N' | null;
}

export interface EmployeeDropdownOption extends DropdownOption {
  name?: string;
}

/**
 * Function to extract the property from an object based on a property access string.
 */
export const getValueFromFieldArrayByString = <T,>(object: Record<string, any>, string: string): T | undefined => {
  string = string.replaceAll(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  string = string.replace(/^\./, ''); // strip a leading dot
  const a = string.split('.');
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (k in object) {
      object = object[k];
    } else {
      return undefined;
    }
  }
  return object as T | undefined;
};

interface ValidatedDropdownProps {
  label: any;
  name: string;
  options: DropdownOption[];
  id?: string;
  isMultiChoice?: boolean;
  isDisabled?: boolean;
  loading?: boolean;
  action?: (result: any) => void;
  backgroundColor?: string;
  propFormikContext?: any;
  restTextfieldProps?: any;
  isMandatory?: boolean;
}

/**
 * A dropdown field component.
 * @param label The displayed label (helper text) of the field.
 * @param name The name to be used as the field name in the Formik form.
 * @param options The possible options for the dropdown.
 * @param isMultiChoice Set whether multiple choices are allowed.
 * @param isDisabled Set whether the field is disabled.
 * @param id A custom ID given for the dropdown.
 * @param loading Indicates whether the field values are being loaded.
 * @param action Action to be executed on value change, if not default.
 * @param propFormikContext Formik context to be used when the dropdown is in a different context.
 */

const ValidatedDropdown: React.FC<ValidatedDropdownProps & Partial<AutocompleteProps<any, any, any, any>>> = ({
  label,
  name,
  options,
  id,
  isMultiChoice = false,
  isDisabled = false,
  loading,
  action,
  backgroundColor,
  propFormikContext,
  restTextfieldProps,
  isMandatory,
  ...rest
}) => {
  const fc = useFormikContext();
  const formikContext = propFormikContext ?? fc;
  const itemValue =
    name.includes('[') || name.includes('.')
      ? getValueFromFieldArrayByString(formikContext.values, name)
      : formikContext.values[`${name}`];

  return (
    <>
      <InputLabel
        id={`${name}-input-label`}
        required={isMandatory}
        disabled={isDisabled}
        htmlFor={`${name}-autocomplete`}
        error={
          Object.prototype.hasOwnProperty.call(formikContext.touched, name) &&
          Object.prototype.hasOwnProperty.call(formikContext.errors, name)
        }
      >
        {label}
      </InputLabel>
      <Autocomplete
        key={name}
        popupIcon={isDisabled ? null : <DropdownIcon />}
        options={options ?? []}
        data-testid={id ? `${id}-autocomplete` : `${name}-autocomplete`}
        id={`${name}-autocomplete`}
        disabled={isDisabled}
        disableCloseOnSelect={isMultiChoice}
        loading={loading}
        getOptionLabel={(option) => {
          return option?.label;
        }}
        value={
          loading || !options
            ? null
            : isMultiChoice
              ? (options.filter((option) => itemValue && itemValue.includes(option.value)) ?? null)
              : (options.find((option) => option.value === itemValue) ?? null)
        }
        onChange={(_, value) => {
          if (action) {
            action(value);
          } else {
            formikContext.setFieldValue(
              name,
              isMultiChoice ? value.map((v: DropdownOption) => v.value) : value ? value.value : value,
            );
            formikContext.setFieldTouched(name, true, false);
          }
        }}
        onBlur={() => fc.setFieldTouched(name, true)}
        multiple={isMultiChoice}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagPropsWithoutKey } = getTagProps({ index });
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option?.label}
                id={`${name}-chip-${index}`}
                size="small"
                style={{
                  padding: '0 4px',
                  margin: '0 8px',
                  backgroundColor: 'white',
                }}
                {...tagPropsWithoutKey}
              />
            );
          })
        }
        renderOption={(props, option, { selected }) => (
          <MenuItem {...props} {...(isMultiChoice && { sx: { padding: '0 !important' } })}>
            {isMultiChoice && <Checkbox style={{ marginRight: 8 }} checked={selected} />}
            {option?.label}
          </MenuItem>
        )}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            error={
              Object.prototype.hasOwnProperty.call(formikContext.touched, name) &&
              Object.prototype.hasOwnProperty.call(formikContext.errors, name)
            }
            helperText={Object.prototype.hasOwnProperty.call(formikContext.touched, name) && formikContext.errors[name]}
            fullWidth={true}
            style={{ backgroundColor: backgroundColor }}
            inputProps={{
              ...params.inputProps,
              'data-testid': `${name}-content-input`,
              id: `${name}-content-input`,
              style: { backgroundColor: backgroundColor },
            }}
            variant="filled"
            InputProps={{
              ...params.InputProps,
              style: { backgroundColor: backgroundColor },
            }}
            {...restTextfieldProps}
          />
        )}
        {...rest}
      />
    </>
  );
};

export default ValidatedDropdown;
